import { useMemo, useState } from 'react'

import { addDays, format, subDays } from 'date-fns'
import { es } from 'date-fns/locale'
import { Calendar, ChevronLeft, ChevronRight, DollarSign, PawPrint } from 'lucide-react'

import { useHotelDayReservations } from '@/components/ReservationContext'
import { cn } from '@/shared/lib/styles/class-merge'
import { Button } from '@/shared/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/shared/ui/card'

export default function MobilePanelInterno() {
    const [selectedDate, setSelectedDate] = useState(new Date())
    const { reservations } = useHotelDayReservations(format(selectedDate, 'yyyy-MM-dd'))

    const dailyStats = useMemo(() => {
        const hotelReservations = reservations

        const totalDogs = hotelReservations.reduce((acc, curr) => {
            if (curr.type === 'hotel') {
                return acc + curr.pets.length
            }
            return acc
        }, 0)

        const totalRevenue = hotelReservations.reduce((acc, curr) => {
            return acc + curr.totalPrice
        }, 0)

        const dogsStaying = hotelReservations
            .flatMap(reservation => {
                if (reservation.type === 'hotel') {
                    return reservation.pets.map(pet => ({
                        ...pet,
                        roomNumber: pet.roomNumber,
                        clientName: reservation.client.name,
                        checkOutDate: reservation.checkOutDate,
                        checkInDate: reservation.checkInDate,
                    }))
                }
                return []
            })
            .sort((a, b) => {
                if (a.roomNumber && b.roomNumber) {
                    return a.roomNumber.localeCompare(b.roomNumber)
                }
                return 0
            })

        return {
            totalDogs,
            totalRevenue,
            dogsStaying,
        }
    }, [reservations])

    const handlePreviousDay = () => {
        setSelectedDate(prev => subDays(prev, 1))
    }

    const handleNextDay = () => {
        setSelectedDate(prev => addDays(prev, 1))
    }

    const handleToday = () => {
        setSelectedDate(new Date())
    }

    const isToday = useMemo(() => {
        const today = format(new Date(), 'yyyy-MM-dd')
        const selected = format(selectedDate, 'yyyy-MM-dd')
        return today === selected
    }, [selectedDate])

    return (
        <div className='min-h-screen bg-gradient-to-b from-background to-secondary/20'>
            <div className='space-y-6 p-4 pb-8'>
                {/* Header Section */}
                <div className='flex flex-col space-y-4'>
                    <div className='flex items-center justify-between'>
                        <h1 className='bg-gradient-to-r bg-clip-text text-2xl font-bold text-primary'>
                            Panel de Control
                        </h1>
                        {!isToday && (
                            <Button
                                variant='outline'
                                size='sm'
                                onClick={handleToday}
                                className='border-primary/20 hover:border-primary/40'
                            >
                                <Calendar className='mr-2 h-4 w-4 text-primary' />
                                Hoy
                            </Button>
                        )}
                    </div>

                    {/* Date Navigation */}
                    <div className='flex items-center justify-between rounded-xl border border-border/50 bg-card p-3 shadow-sm'>
                        <Button
                            variant='ghost'
                            size='icon'
                            onClick={handlePreviousDay}
                            className='hover:bg-primary/10 hover:text-primary'
                        >
                            <ChevronLeft className='h-4 w-4' />
                        </Button>
                        <div className='text-lg font-medium'>{format(selectedDate, "d 'de' MMMM", { locale: es })}</div>
                        <Button
                            variant='ghost'
                            size='icon'
                            onClick={handleNextDay}
                            className='hover:bg-primary/10 hover:text-primary'
                        >
                            <ChevronRight className='h-4 w-4' />
                        </Button>
                    </div>
                </div>

                {/* Stats Cards */}
                <div className='grid grid-cols-2 gap-4'>
                    <Card className='border-primary/10 bg-card/50 shadow-lg backdrop-blur-sm'>
                        <CardHeader className='flex flex-row items-center justify-between space-y-0 pb-2'>
                            <CardTitle className='text-sm font-medium text-primary'>Perros</CardTitle>
                            <div className='flex h-8 w-8 items-center justify-center rounded-full bg-primary/10'>
                                <PawPrint className='h-4 w-4 text-primary' />
                            </div>
                        </CardHeader>
                        <CardContent>
                            <div className='text-3xl font-bold'>{dailyStats.totalDogs}</div>
                        </CardContent>
                    </Card>
                    <Card className='border-primary/10 bg-card/50 shadow-lg backdrop-blur-sm'>
                        <CardHeader className='flex flex-row items-center justify-between space-y-0 pb-2'>
                            <CardTitle className='text-sm font-medium text-primary'>Facturación</CardTitle>
                            <div className='flex h-8 w-8 items-center justify-center rounded-full bg-primary/10'>
                                <DollarSign className='h-4 w-4 text-primary' />
                            </div>
                        </CardHeader>
                        <CardContent>
                            <div className='text-3xl font-bold'>
                                {dailyStats.totalRevenue.toLocaleString('es-ES', {
                                    maximumFractionDigits: 2,
                                    minimumFractionDigits: 0,
                                })}
                                €
                            </div>
                        </CardContent>
                    </Card>
                </div>

                {/* Dogs List */}
                <div className='space-y-3'>
                    <div className='pl-1 text-lg font-medium text-primary'>Perros Hospedados</div>
                    <div className='space-y-3'>
                        {dailyStats.dogsStaying.map((dog, index) => {
                            const isNewCheckIn = dog.checkInDate === format(selectedDate, 'yyyy-MM-dd')
                            const isCheckOut = dog.checkOutDate === format(selectedDate, 'yyyy-MM-dd')

                            return (
                                <div
                                    key={`${dog.name}-${index}`}
                                    className={cn(
                                        'flex items-start justify-between rounded-xl border bg-card/50 p-4 shadow-sm backdrop-blur-sm transition-colors',
                                        isNewCheckIn && 'border-emerald-500/20 bg-emerald-500/10',
                                        isCheckOut && 'border-amber-500/20 bg-amber-500/10',
                                        !isNewCheckIn && !isCheckOut && 'border-border/50 hover:bg-muted/50',
                                    )}
                                >
                                    <div>
                                        <div className='flex items-center gap-2 font-medium'>
                                            {dog.name}
                                            {isNewCheckIn && (
                                                <span className='rounded-full bg-emerald-500/20 px-2 py-0.5 text-xs text-emerald-700'>
                                                    Nuevo
                                                </span>
                                            )}
                                            {isCheckOut && (
                                                <span className='rounded-full bg-amber-500/20 px-2 py-0.5 text-xs text-amber-700'>
                                                    Sale hoy
                                                </span>
                                            )}
                                        </div>
                                        <div className='text-sm text-muted-foreground'>{dog.breed}</div>
                                        <div className='mt-1 text-xs text-muted-foreground'>{dog.clientName}</div>
                                    </div>
                                    <div className='text-right'>
                                        <div className='font-medium text-primary'>{dog.roomNumber || 'Sin hab.'}</div>
                                        <div className='mt-1 text-xs text-muted-foreground'>
                                            Sale: {format(new Date(dog.checkOutDate), 'dd/MM')}
                                        </div>
                                    </div>
                                </div>
                            )
                        })}
                        {dailyStats.dogsStaying.length === 0 && (
                            <div className='rounded-xl border border-border/50 bg-card/50 py-8 text-center text-muted-foreground backdrop-blur-sm'>
                                No hay perros hospedados en esta fecha
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    )
}
